import React, { useState } from 'react';
import { Switch, Route, Redirect, Link as NavLink } from 'react-router-dom';
import { Query } from 'react-apollo';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Spin from 'components/common/Spin';
import { GET_ESSAY_TOPICS } from 'graphql/essays';
import Topics from './components/Topics';
import TopicFormModal from './components/TopicFormModal';
import Prompts from './components/Prompts';
import PersonalStatement from './components/PersonalStatement';
import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';

const useStyles = makeStyles((theme) => ({
  tabs: {
    'marginBottom': theme.spacing(4),
  },
  emptyCard: {
    textAlign: 'center',
    padding: theme.spacing(1),
    maxWidth: 500,
    margin: 'auto',
  },
}));

const NavTab = withStyles((theme) => ({
  root: {
    minWidth: 72,
  }
}))(Tab);

const CommonApp = ({ location: { pathname }, student, readOnly }) => {
  const classes = useStyles();
  const user = useSelector((state) => state.user.user);
  const [showAddTopic, toggleAddTopic] = useState(false);
  const [editing, setEditing] = useState();
  const isStudent = user.type === 'student';
  const queryVariables = { userId: isStudent ? user.id : student.id };
  const routePrefix = isStudent ? '' : `/students/${student.id}`;
  const openAddTopic = () => toggleAddTopic(true);

  const onEdit = (row) => {
    setEditing(row);
    toggleAddTopic(true);
  };

  const onCloseModal = () => {
    setEditing();
    toggleAddTopic(false);
  };

  return (
    <div>
      <Helmet title="Personal Statement" />
      <Box className={classes.tabs}>
        <Tabs value={pathname}
              indicatorColor="primary"
              textColor="primary">
          <NavTab
            component={NavLink}
            to={`${routePrefix}/common-app/brainstorm`}
            value={`${routePrefix}/common-app/brainstorm`}
            label="Brainstorm"
          />
          <NavTab
            component={NavLink}
            to={`${routePrefix}/common-app/prompts`}
            value={`${routePrefix}/common-app/prompts`}
            label="Prompt"
          />
          <NavTab
            component={NavLink}
            to={`${routePrefix}/common-app/statements`}
            value={`${routePrefix}/common-app/statements`}
            label="Personal Statement"
          />
        </Tabs>
      </Box>
      <Switch>
        <Route
          path={`${routePrefix}/common-app/brainstorm`}
          render={() => (
            <Query query={GET_ESSAY_TOPICS} variables={queryVariables}>
              {({ data, loading }) => {
                if (loading) return <Spin spinning linear />;
                const { essay_topics } = data;
                return (
                  <>
                    {essay_topics.length ? (
                      <Box>
                        <Grid container justify="center">
                          <Grid item xs={12} sm={8} md={6}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={2}>
                              <Typography variant="smallBold" color="textSecondary">
                                {essay_topics.length} topic(s) added
                              </Typography>
                              {user.type !== 'parent' && (
                                <Button
                                  variant={'contained'}
                                  color="primary"
                                  onClick={openAddTopic}
                                >
                                  Add New Topic
                                </Button>
                              )}
                            </Box>
                            <Topics
                              readOnly={user.type === 'parent'}
                              student={student}
                              topics={essay_topics}
                              onEdit={onEdit}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    ) : (
                      <Card className={classes.emptyCard}>
                        <CardContent>
                          <Typography gutterBottom variant="subtitle1">
                            No New Topics Added
                          </Typography>
                          <Typography
                            component="div"
                            variant="caption"
                          >
                            Add at least 10 essay topic ideas, at maximum 30
                          </Typography>
                          {user.type !== 'parent' && (
                            <Box marginTop={4}>
                              <Button
                                variant={'contained'}
                                color="primary"
                                size={'large'}
                                onClick={openAddTopic}
                              >
                                Add New Topic
                              </Button>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </>
                );
              }}
            </Query>
          )}
        />
        <Route
          path={`${routePrefix}/common-app/prompts`}
          component={() => <Prompts student={student} />}
        />
        <Route
          path={`${routePrefix}/common-app/statements`}
          component={() => <PersonalStatement student={student} readOnly={readOnly} />}
        />
        <Redirect
          from={`${routePrefix}/common-app`}
          to={`${routePrefix}/common-app/statements`}
        />
      </Switch>
      {showAddTopic && (
        <TopicFormModal
          student={student}
          topic={editing}
          onClose={onCloseModal}
        />
      )}
    </div>
  );
};

export default CommonApp;
